import { Check, Close } from "@mui/icons-material";
import { Stack, Typography, Box } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { IconComponent } from "src/shared/components";
import { ChipComponent } from "src/shared/components/ChipComponent";
import { PartnerLabel } from "src/shared/components/PartnerLabel";
import { EIconNames } from "src/shared/types/iconNames";
import { IFormatDebtDaysResult } from "../types";

const formatDebtDays = (days?: number): IFormatDebtDaysResult => {
  if (!days || days <= 0) {
    return {
      text: "",
      color: "inherit",
    };
  }

  if (days <= 30) {
    return {
      text: `${days} дн.`,
      color: "#000000",
    };
  }

  const DAYS_IN_MONTH = 30;
  const DAYS_IN_YEAR = 360;

  let remainingDays = days;

  const years = Math.floor(remainingDays / DAYS_IN_YEAR);
  remainingDays %= DAYS_IN_YEAR;

  const months = Math.floor(remainingDays / DAYS_IN_MONTH);
  remainingDays %= DAYS_IN_MONTH;

  const parts: string[] = [];

  if (years > 0) parts.push(`${years} г.`);
  if (months > 0) parts.push(`${months} мес.`);
  if (remainingDays > 0) parts.push(`${remainingDays} дн.`);

  return {
    text: parts.join(" "),
    color: "#DC2626",
  };
};

export const columns: GridColDef[] = [
  {
    headerName: "Партнер",
    field: "partner",
    flex: 1,
    sortable: false,
    renderCell: ({ row }) => (
      <PartnerLabel
        name={row.partner}
        organizationForm={row.organization_form?.value}
      />
    ),
  },
  {
    headerName: "Компания",
    field: "company",
    flex: 1.2,
    sortable: false,
    renderCell: (params) => {
      const isIcontext = params.row.company.toLowerCase() === "icontext";

      return (
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{ height: "100%" }}
        >
          <IconComponent
            sx={{ width: 20, height: 20, display: "block" }}
            iconname={
              isIcontext ? EIconNames.ICONTEXT : EIconNames.WEBCOM_LOGO_SHORT
            }
          />
          <Typography
            variant="body2"
            sx={{ lineHeight: 1, display: "flex", alignItems: "center" }}
          >
            {params.row.company}
          </Typography>
        </Stack>
      );
    },
  },
  {
    headerName: "КД-А Партнера",
    field: "contract",
    flex: 1,
    sortable: false,
    renderCell: (params) => (
      <Link
        to={`/crm/desk/partners/${params.row.partner_id}/contracts/`}
        color="primary"
        target="_blank"
      >
        {params.value}
      </Link>
    ),
  },
  {
    headerName: "Документ + Способ подписания",
    field: "document_type",
    flex: 1.5,
    sortable: false,

    renderCell: (params) => {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={3}
            sx={{ width: "100%", height: "100%" }}
          >
            <Typography
              variant="body2"
              noWrap
              sx={{
                display: "flex",
                alignItems: "center",
                lineHeight: 1,
              }}
            >
              {params.value}
            </Typography>
            {params.row.document_status === "edin" && (
              <IconComponent
                sx={{ width: 16, height: 16, position: "relative", top: 2 }}
                iconname={EIconNames.ELECTRON_ICON}
              />
            )}
          </Stack>
        </Box>
      );
    },
  },
  {
    headerName: "Дата документа",
    field: "document_date",
    flex: 1,
    align: "center",
    sortable: false,

    valueFormatter: (value: string | null) =>
      value && dayjs(value).format("DD.MM.YYYY"),
  },
  {
    headerName: "Статус документа",
    field: "doc_status",
    flex: 1,
    sortable: false,
    renderCell({ row }) {
      const status = row.doc_status as
        | "В наличии"
        | "Не требуется"
        | "Отсутствует";
      const map: Record<
        "В наличии" | "Не требуется" | "Отсутствует",
        { icon: React.ElementType; color: "success" | "error" } | null
      > = {
        "В наличии": { icon: Check, color: "success" },
        Отсутствует: { icon: Close, color: "error" },
        "Не требуется": null,
      };

      const config = map[status];

      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
          }}
        >
          {config ? <config.icon color={config.color} /> : "-"}
        </Box>
      );
    },
  },
  {
    headerName: "Срок задолженности",
    field: "debt_days",
    sortable: false,
    flex: 1,
    renderCell: (params) => {
      const { text, color } = formatDebtDays(Number(params.value));

      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
          }}
        >
          <Typography variant="body2" sx={{ color }}>
            {text}
          </Typography>
        </Box>
      );
    },
  },
  {
    headerName: "Статус КДА",
    field: "contract_status",
    flex: 1,
    sortable: false,
    renderCell: (params) => {
      const isActive = params.value === "Действующий";

      return (
        <ChipComponent
          label={params.value}
          backgroundColor={isActive ? "#DCFCE7" : "#FFEDD5"}
          color={isActive ? "#16A34A" : "#F54A00"}
        />
      );
    },
  },
];
